import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import {
    allColumns,
    allColumnsWithProvider,
    extendProductColumns,
    REQUIRED_RECEIVINGS_GRID_ALL_PROVIDERS_COLUMNS,
    REQUIRED_RECEIVINGS_GRID_COLUMNS
} from './constants';
import Table from 'src/components/Table';
import {
    ExportTypeType,
    OnChangeOptionsType,
    RowActionPropsType,
    SelectRowType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType
} from 'src/types/table';
import { ReceivingIcon } from 'src/components/SkladonIcon/Icons';
import { useDebounce } from 'src/utils/debounce';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import filterUnique from 'src/utils/filterUnique';
import { ReceivingType } from 'src/types/receiving';
import { useCheckPermission } from 'src/utils/checkPermission';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { getReceivingsGridFetch } from 'src/resources/receivingsGrid/reducer';
import { patchMultipleReceivingsFetch, synchronizeReceivingsDCIXFetch } from 'src/resources/receivingsUpdate/reducer';
import checkOrChangeProvider from 'src/utils/checkOrChangeProvider';
import {
    RECEIVING_GRID_ALL_PROVIDERS_URL,
    RECEIVING_GRID_PRODUCTS_URL,
    RECEIVING_GRID_URL
} from 'src/resources/receivingsGrid/sagas';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { disabbleMessages } from 'src/resources/alertMessages/reducer';
import getStatusColor from 'src/utils/getStatusColor';
import colorOptionFce from 'src/utils/colorOptionFce';
import translateUrgentValue from '../../utils/translateUrgentValue';

export const SESSION_NAME = 'receivingsTable';

const customRender = (
    column: TableColumnType,
    row: TableRowType,
    statusCodesObject: ConstantsCodesObjectType
): ReactNode | string => {
    switch (column.key) {
        case 'STATUS':
            return (
                <span style={{ color: getStatusColor(statusCodesObject[row.STATUS]?.color) }}>
                    {statusCodesObject[row.STATUS]?.label}
                </span>
            );
        case 'URGENT':
            return (
                <span>
                    {translate(translateUrgentValue(row[column.key]))}
                </span>
            );
        default:
            return row[column.key];
    }
};

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ID', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: []
};

const AllReceivingsContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');

    const [allProviders, setAllProviders] = useState<boolean>(false);
    const [formatedData, setFormatedData] = useState<ReceivingType[]>([]);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);
    const [selectedRows, setSelectedRows] = useState<SelectRowType[]>([]);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const loading = useSelector((state: RootStoreType) => state.receivingsGrid.loading);
    const loadingUpdate = useSelector((state: RootStoreType) => state.receivingsUpdate.loading);
    const loadingUpdateMultiple = useSelector((state: RootStoreType) => state.receivingsUpdate.loadingMultiple);
    const receivingsData = useSelector((state: RootStoreType) => state.receivingsGrid.data);

    const provider = useSelector((state: RootStoreType) => state.provider.data);

    const receivingStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingStatusCodes);
    const receivingStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.receivingStatusCodes
    );
    const receivingDeliveryTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.receivingDeliveryTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const receivingUrgentCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingUrgentCodes);
    const driverCompanyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.driverCompanyCodes);
    const receivingPriorityCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingPriorityCodes);
    const receivingTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingTypeCodes);
    const incotermsCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.incotermsCodes);
    const urgentCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.receivingUrgentCodes);

    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const providers = useSelector((state: RootStoreType) => state.user.data?.providers);

    const permissionAllProviders = useCheckPermission('RECEIVING.LISTALLPROVIDERS');
    const permissionReceivingCreate = useCheckPermission('RECEIVING.CREATE');
    const permissionReceivingUpdate = useCheckPermission('RECEIVING.UPDATE');
    const permissionReceivingDetail = useCheckPermission('RECEIVING.DETAIL');
    const permissionSynchronizeDcix = useCheckPermission('RECEIVING.ACTION.DCIX.IMPORT');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('receivings.title') }));
    }, []);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        loadTableData();
    }, [debouncedTableOptions]);

    useEffect(() => {
        if (receivingsData?.data) {
            setFormatedData(getFormatedData(receivingsData?.data || []));
        }
    }, [receivingsData?.data]);

    const getFormatedRow = (
        row: TableRowType,
        options: { providers?: boolean; productItemDiff?: boolean }
    ): TableRowType => {
        const rowOut = { ...row };

        if (options.providers) {
            const provider = providers?.find((provider) => provider.ESHOP_KEY === row.ESHOP_KEY);
            rowOut.providerName = provider?.COMPANY_NAME;
        }

        if (options.productItemDiff) {
            const productDiff = (row.PRODUCT_RECEIVED || 0) - (row.PRODUCT_QUANTITY || 0);
            rowOut.PRODUCT_DIFF_QUANTITY = productDiff;
        }

        return rowOut;
    };

    const getFormatedData = (data: ReceivingType[]): ReceivingType[] => {
        if (!allProviders) {
            return data;
        }
        return data.map((item) => {
            return getFormatedRow(item, { providers: true });
        });
    };

    const getAllGridParams = () => ({
        filters: [[...(tableOptions.filters || [])]],
        sorts: [tableOptions.sort],
        page: tableOptions.page,
        items: tableOptions.items,
        columns: [
            ...(allProviders ? REQUIRED_RECEIVINGS_GRID_ALL_PROVIDERS_COLUMNS : REQUIRED_RECEIVINGS_GRID_COLUMNS),
            ...tableOptions.columns.filter((column) => !column.allwaysShow).map((column) => column.key)
        ].filter(filterUnique)
    });

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(getReceivingsGridFetch({ params: getAllGridParams(), allProviders }));
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const handleExport = (type: ExportTypeType, withProducts?: boolean) => {
        let fileName = 'receivings.action.exportFileName';
        if (withProducts) {
            fileName = 'receivings.action.exportWithProductsFileName';
        }
        tableRef.current?.exportTableGrid(
            type,
            translate(fileName),
            getAllGridParams,
            allProviders
                ? RECEIVING_GRID_ALL_PROVIDERS_URL
                : withProducts
                    ? RECEIVING_GRID_PRODUCTS_URL
                    : RECEIVING_GRID_URL,
            withProducts ? extendProductColumns : undefined,
            {
                rowFunction:
                    allProviders || withProducts
                        ? (row) => getFormatedRow(row, { providers: allProviders, productItemDiff: withProducts })
                        : undefined
            }
        );
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const changeProvider = (row: TableRowType) => {
        if (allProviders && providers && provider) {
            if (checkOrChangeProvider(providers, provider, 'ESHOP_KEY', row.ESHOP_KEY)) {
                dispatch(disabbleMessages());
            }
        }
    };

    const reloadTableData = () => {
        dispatch(getReceivingsGridFetch(undefined));
    };

    const handleSynchronizeDCIX = () => {
        setConfirmParams({
            title: translate('receivings.action.synchronizeDcix'),
            content: translate('receivings.action.synchronizeDcixText', { count: selectedRows.length }),
            action: () => {
                dispatch(
                    synchronizeReceivingsDCIXFetch(
                        receivingsData?.data.filter((receiving) => selectedRows.includes(String(receiving.ID)))
                    )
                );
            }
        });
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <ReceivingIcon />,
            label: translate('receivings.action.receivingDetail'),
            getLink: (row) => `/receivings/${row.ID}/`,
            onClick: changeProvider,
            hiddenFce: () => !permissionReceivingDetail
        }
    ];

    const handleMassChangeStatus = (statuses: ConstantsCodeType[]) => {
        setConfirmParams({
            title: translate('receivings.action.changeStatuses'),
            content: translate('receivings.action.changeStatusesText', { count: selectedRows.length }),
            options: statuses,
            optionsValue: statuses[0].value,
            action: (value: ExportTypeType) => {
                dispatch(
                    patchMultipleReceivingsFetch({
                        receivings: receivingsData?.data.filter((order) => selectedRows.includes(String(order.ID))),
                        data: { STATUS: value }
                    })
                );
            }
        });
    };

    const tableSelectOptions: {
        [key: string]: ConstantsCodeType[];
    } = {
        receivingStatusCodes,
        receivingDeliveryTypeCodes,
        boolNumberCodes,
        receivingUrgentCodes,
        driverCompanyCodes,
        receivingTypeCodes,
        incotermsCodes,
        receivingPriorityCodes,
        urgentCodes
    };

    return (
        <>
            <TopBlock
                handleExport={handleExport}
                allProviders={allProviders}
                setAllProviders={setAllProviders}
                reloadTableData={reloadTableData}
                selectedRows={selectedRows}
                permissionReceivingCreate={permissionReceivingCreate}
                permissionSynchronizeDcix={permissionSynchronizeDcix}
                handleSynchronizeDCIX={handleSynchronizeDCIX}
                permissionAllProviders={permissionAllProviders}
                receivingStatusCodes={receivingStatusCodes}
                receivingStatusCodesObject={receivingStatusCodesObject}
                permissionReceivingUpdate={permissionReceivingUpdate}
                handleMassChangeStatus={handleMassChangeStatus}
                receivingsDataData={receivingsData?.data || []}
            />
            <Table
                allColumns={allProviders ? allColumnsWithProvider : allColumns}
                ref={tableRef}
                rowIdKey="ID"
                rows={formatedData}
                loading={loading || loadingUpdate || loadingUpdateMultiple}
                columsLabelPrefix="receivings.table"
                options={tableOptions}
                totalCount={receivingsData?.totalCount}
                onChangeOptions={handleChangeOptions}
                rowActions={tableRowActions}
                selectOptions={tableSelectOptions}
                userSettingsName="showReceivingsTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                optionsSessionName={SESSION_NAME}
                selectedRows={!allProviders ? selectedRows : undefined}
                setSelectedRows={!allProviders ? setSelectedRows : undefined}
                fullHeight
                customRender={(column, row) => customRender(column, row, receivingStatusCodesObject)}
                optionStyleFce={colorOptionFce}
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default AllReceivingsContainer;
